import {
  Alert,
  Autocomplete,
  Badge,
  Button,
  Combobox,
  DatePicker,
  Input,
  Select,
  type BadgeVariant,
} from "@marekl/adminer/components";
import { dayjs } from "@marekl/adminer/datetime";
import { formatDate } from "@marekl/adminer/format";
import { ChevronDown, ChevronRight, Pencil, Search, TriangleAlert, UserPlus, X } from "lucide-react";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
} from "react";
import { RoleBadge, type RoleBadgeRole } from "../../../components/RoleBadge";
import { birthNumberHint } from "../../../lib/birthNumber";
import { COUNTRY_OPTIONS } from "../../../lib/countries";
import {
  BIRTH_NUMBER_MISSING_HINT,
  IDENTITY_FIELDS,
  LEADER_ROLE_OPTIONS,
  REQUIRED_DATA_FIELDS,
  ROLE_LABELS,
  ROLE_OPTIONS,
} from "../TeamDetailPage/memberFormSchema";
import type { Membership, MembershipRole } from "../../../lib/queries/memberships";
import type { RosterMergeAction } from "./draftReducer";
import { FIELD_LABELS, displayValue } from "./draftFields";
import type { DraftPerson, DraftRow, FieldConflict, RowErrors } from "./draftTypes";
import { isBlankNewRow } from "./draftValidation";
import styles from "./DraftRoster.module.css";

/**
 * The soupiska as a list: one read-only line per person, one person open for
 * editing at a time, and an always-open form for a new member at the end.
 *
 * Replaces a spreadsheet-like grid that ran to eleven input columns and
 * scrolled sideways, which leaders did not realise it did. A line is text,
 * not controls, so each column can stack two values and the whole record
 * fits the page width; the open person gets a form with a label on every
 * field, so nothing has to be looked up in a header.
 *
 * Display order is the draft's own order (roster order from hydration, staged
 * rows appended), so a person added here appears at the end, right above the
 * form they came from, and never jumps once a surname is typed.
 */

export interface DraftRosterProps {
  rows: DraftRow[];
  errors: Map<string, RowErrors>;
  /** True for admin / members_admin, the only roles that may correct identity. */
  canEditIdentity: boolean;
  onCell: (key: string, field: keyof DraftPerson, value: string | DraftPerson["birth_date"]) => void;
  onRoles: (key: string, roles: MembershipRole[]) => void;
  onRemove: (row: DraftRow) => void;
  /**
   * Last-season candidates matching a typed query: the new-member form's
   * Příjmení suggests them, so a returning member is a few letters away.
   */
  suggest?: (query: string) => Membership[];
  /** A suggestion was picked: stage the transfer, drop the typed row. */
  onPick?: (row: DraftRow, m: Membership) => void;
  /** Answers to what a merge with the server left open (draftRebase). */
  onMerge?: (action: RosterMergeAction) => void;
}

const noMerge = () => {};

export interface DraftRosterHandle {
  /** Show a person (the draft bar's error list): open, scroll to, flash. */
  reveal: (key: string) => void;
}

/**
 * identityCellLocked mirrors the backend's identity rules; it never replaces
 * them. Jméno, příjmení, rodné číslo and datum narození of someone who already
 * exists are a federation-level correction, not roster upkeep.
 *
 * The lock is per field rather than per person: a field that already holds a
 * value is frozen, an empty one stays open, so a record can be completed but
 * never altered. The leader's own identity is locked whether filled or not
 * (ErrLeaderIdentityLocked).
 */
function identityCellLocked(row: DraftRow, field: keyof DraftPerson, canEditIdentity: boolean): boolean {
  if (row.origin.kind === "new") return false;
  if (canEditIdentity) return false;
  if (!(IDENTITY_FIELDS as readonly string[]).includes(field)) return false;
  if (row.isLeader) return true;
  const current = row.baseline?.[field as keyof NonNullable<typeof row.baseline>];
  return Boolean(current);
}

/**
 * What the registration still wants of a person. The rodné číslo is listed
 * but is only ever a warning (REQUIRED_DATA_FIELDS); the rest block a submit
 * once the row is touched, which errorsByRow decides, not this.
 */
function missingFields(row: DraftRow): string[] {
  if (row.removed || isBlankNewRow(row)) return [];
  const p = row.person;
  const out: string[] = [];
  if (!p.birth_date) out.push("datum narození");
  if (!p.birth_number.trim()) out.push("rodné číslo");
  const labels: Record<(typeof REQUIRED_DATA_FIELDS)[number], string> = {
    street: "ulice",
    city: "město",
    postal_code: "PSČ",
  };
  for (const f of REQUIRED_DATA_FIELDS) if (!p[f].trim()) out.push(labels[f]);
  return out;
}

/**
 * What is wrong with a rodné číslo that IS filled in, judged as the form
 * judges it once focus leaves the field. Only ever a warning (docs/rodne-cislo.md
 * §5), but a leader has to see it without opening every person: the grid that
 * this list replaced showed it on every row. An empty number is a gap, said by
 * missingFields.
 */
function birthNumberIssue(row: DraftRow, rowErrors?: RowErrors): string | null {
  // A server refusal of the number speaks for itself (K opravě), as it does in
  // the open form; the local check would put a different sentence beside it.
  if (row.removed || isBlankNewRow(row) || rowErrors?.["person.birth_number"]) return null;
  const p = row.person;
  return birthNumberHint(p.birth_number, p.birth_date ? p.birth_date.format("YYYY-MM-DD") : undefined);
}

// A number the viewer cannot change (filled in, below members_admin) is still
// flagged on the line, but it does not make the person something to work
// through: the filter would collect rows only the federation can correct.
function needsAttention(row: DraftRow, rowErrors: RowErrors | undefined, canEditIdentity: boolean): boolean {
  return (
    Boolean(rowErrors && Object.keys(rowErrors).length > 0) ||
    missingFields(row).length > 0 ||
    (birthNumberIssue(row, rowErrors) !== null && !identityCellLocked(row, "birth_number", canEditIdentity))
  );
}

/**
 * The badge on a closed person, or none. Only what asks for something is
 * badged: a list where every line carries „Kompletní", „Převedený" or
 * „Upravený" hides the few lines that need the leader among the many that
 * do not.
 */
function status(
  row: DraftRow,
  rowErrors: RowErrors | undefined,
): { label: string; variant: BadgeVariant } | null {
  if (row.removed) return { label: "K odebrání", variant: "muted" };
  // Emptied out: nothing here is sent, and closing it drops the line.
  if (isBlankNewRow(row)) return { label: "Prázdný", variant: "muted" };
  if (rowErrors && Object.keys(rowErrors).length > 0) return { label: "K opravě", variant: "err" };
  const missing = missingFields(row);
  if (missing.length === 1 && missing[0] === "rodné číslo") return { label: "Chybí RČ", variant: "warn" };
  if (missing.length > 0) return { label: "Chybí údaje", variant: "warn" };
  if (birthNumberIssue(row, rowErrors)) return { label: "Zkontrolovat RČ", variant: "warn" };
  return null;
}

const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const NO_ERRORS: RowErrors = {};

export const DraftRoster = forwardRef<DraftRosterHandle, DraftRosterProps>(function DraftRoster(
  props,
  ref,
) {
  const { rows, errors } = props;
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // „Chybí údaje" lists the people who needed something WHEN it was switched
  // on. Snapshotting is what keeps a person on screen while their last gap is
  // being filled; recomputing per keystroke would whisk them away mid-word.
  const [attentionKeys, setAttentionKeys] = useState<{
    needs: ReadonlySet<string>;
    seen: ReadonlySet<string>;
  } | null>(null);

  // The new-member form follows one row by key. The reducer appends a fresh
  // blank row on the first keystroke, so „the trailing blank row" moves on
  // under the form; the form stays with the row being typed until focus
  // leaves it (commitNew), and only then picks up the next blank.
  // The reducer keeps the LAST row as the free blank and nothing else
  // (withTrailingBlank), so a blank row anywhere before it is a person who
  // was emptied out while being edited: listed, not hidden. Derived from the
  // rows alone, so it survives this component being remounted.
  const lastRow = rows.at(-1);
  const trailingBlank = lastRow && isBlankNewRow(lastRow) ? lastRow.key : null;
  const [formKeyState, setFormKey] = useState<string | null>(null);
  const formKey =
    formKeyState && rows.some((r) => r.key === formKeyState) ? formKeyState : trailingBlank;
  const formRow = rows.find((r) => r.key === formKey) ?? null;

  const listed = useMemo(
    () => rows.filter((r) => r.key !== formKey && (!isBlankNewRow(r) || r !== lastRow)),
    [rows, formKey, lastRow],
  );
  // The snapshot decides only for people it saw. Somebody listed AFTER the
  // filter was switched on (added from the form, say) is judged live, or a
  // half-filled newcomer would be hidden by the very filter for gaps.
  const shown = useMemo(
    () =>
      attentionKeys
        ? listed.filter(
            (r) =>
              attentionKeys.needs.has(r.key) ||
              r.key === openKey ||
              (!attentionKeys.seen.has(r.key) && needsAttention(r, errors.get(r.key), props.canEditIdentity)),
          )
        : listed,
    [listed, attentionKeys, openKey, errors, props.canEditIdentity],
  );
  const attentionCount = useMemo(
    () => listed.filter((r) => needsAttention(r, errors.get(r.key), props.canEditIdentity)).length,
    [listed, errors, props.canEditIdentity],
  );

  const listRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const focusFirstField = useCallback((key: string) => {
    // After the render that opened it. The first EMPTY editable field wins:
    // a person is usually opened to fill a gap.
    requestAnimationFrame(() => {
      const host = listRef.current?.querySelector<HTMLElement>(`[data-row-key="${key}"]`);
      const inputs = Array.from(
        host?.querySelectorAll<HTMLInputElement>("[data-person-form] input:not(:disabled)") ?? [],
      );
      (inputs.find((i) => !i.value) ?? inputs[0])?.focus({ preventScroll: true });
    });
  }, []);

  // Read through a ref so the callbacks handed to every person stay stable:
  // a new identity per keystroke would re-render the whole roster.
  const latest = useRef({ openKey, rows });
  latest.current = { openKey, rows };

  // Leaving a person who was added here and then emptied out drops them: an
  // empty line is not a member, and nothing else would ever remove it.
  const { onRemove } = props;
  const leave = useCallback(() => {
    const { openKey, rows } = latest.current;
    const row = rows.find((r) => r.key === openKey);
    if (row && isBlankNewRow(row)) onRemove(row);
  }, [onRemove]);

  const toggle = useCallback(
    (key: string) => {
      leave();
      if (latest.current.openKey === key) {
        setOpenKey(null);
        return;
      }
      setOpenKey(key);
      focusFirstField(key);
    },
    [focusFirstField, leave],
  );

  useImperativeHandle(
    ref,
    () => ({
      reveal: (key: string) => {
        setAttentionKeys(null);
        if (key === formKey) {
          formRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
          return;
        }
        setOpenKey(key);
        requestAnimationFrame(() => {
          const el = listRef.current?.querySelector(`[data-row-key="${key}"]`);
          if (!el) return;
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.setAttribute("data-flash", "");
          window.setTimeout(() => el.removeAttribute("data-flash"), 1600);
        });
      },
    }),
    [formKey],
  );

  // Search highlights, never filters: the list keeps its shape, so a leader
  // who searches for one person still sees where they sit among the rest.
  const q = fold(query.trim());
  const hits = useMemo(() => {
    if (!q) return new Set<string>();
    return new Set(
      listed
        .filter((r) => {
          const a = fold(`${r.person.last_name} ${r.person.first_name}`);
          const b = fold(`${r.person.first_name} ${r.person.last_name}`);
          return a.includes(q) || b.includes(q);
        })
        .map((r) => r.key),
    );
  }, [listed, q]);
  const firstHit = shown.find((r) => hits.has(r.key))?.key;
  useEffect(() => {
    if (!firstHit) return;
    listRef.current
      ?.querySelector(`[data-row-key="${firstHit}"]`)
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [firstHit]);

  // Leaving the form with anything typed is what puts the person on the
  // roster: the row already exists in the draft, so „committing" only moves
  // the form on to the next blank row. On leaving, never per keystroke, so the
  // form does not move under the cursor. Popovers inside it (the date picker,
  // the role list, the surname suggestions) are not portaled and count as
  // inside.
  // „Přidat" says out loud what leaving the form does anyway, and puts the
  // cursor back into the empty form for the next person. „Smazat" throws
  // away what was typed: the row is the form's own, so it is simply dropped.
  // The form re-mounts on its next row, so focus moves once that render is
  // in, not in the click handler.
  const refocusForm = useRef(false);
  useEffect(() => {
    if (!refocusForm.current) return;
    refocusForm.current = false;
    formRef.current?.querySelector<HTMLInputElement>("input")?.focus({ preventScroll: true });
  }, [formKey]);
  const addFromForm = () => {
    if (!formRow || isBlankNewRow(formRow)) return;
    setFormKey(null);
    refocusForm.current = true;
  };
  const clearForm = () => {
    if (!formRow || isBlankNewRow(formRow)) return;
    onRemove(formRow);
    setFormKey(null);
    refocusForm.current = true;
  };

  const onFormBlur = (e: FocusEvent<HTMLDivElement>) => {
    const to = e.relatedTarget as Node | null;
    if (to && e.currentTarget.contains(to)) return;
    // Switching to another window is not leaving the form: focus comes back
    // to the same field, and the half-typed person must still be there.
    if (!to && !document.hasFocus()) return;
    if (!formRow) return;
    if (isBlankNewRow(formRow)) {
      // Typed into and erased again: once focus has left, the row is nobody.
      // Left in place it would come back as an empty listed line whenever the
      // roster remounts (a blank before the last row reads as an emptied
      // person). The free blank at the end is never dropped.
      if (formRow !== rows.at(-1)) {
        onRemove(formRow);
        setFormKey(null);
      }
      return;
    }
    setFormKey(null);
  };

  // Pinned in the same event as the edit: the reducer appends the next blank
  // row in that very dispatch, and without the pin the form would follow it
  // and lose the input under the cursor.
  const { onCell, onRoles } = props;
  const formOnCell = useCallback<DraftRosterProps["onCell"]>(
    (key, field, value) => {
      setFormKey(key);
      onCell(key, field, value);
    },
    [onCell],
  );
  const formOnRoles = useCallback<DraftRosterProps["onRoles"]>(
    (key, roles) => {
      setFormKey(key);
      onRoles(key, roles);
    },
    [onRoles],
  );

  const jumpToForm = () => {
    formRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    formRef.current
      ?.querySelector<HTMLInputElement>("input")
      ?.focus({ preventScroll: true });
  };

  // A suggestion picked in the form drops the typed row (onPick); the form
  // re-mounts on the next blank, so focus goes back to its Příjmení.
  const onPick = props.onPick;
  const pickInForm = useMemo(
    () =>
      onPick
        ? (row: DraftRow, m: Membership) => {
            onPick(row, m);
            requestAnimationFrame(() => formRef.current?.querySelector<HTMLInputElement>("input")?.focus());
          }
        : undefined,
    [onPick],
  );

  return (
    <div className={styles.roster}>
      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Input
            type="search"
            aria-label="Najít člena"
            placeholder="Najít člena…"
            prefix={<Search size={14} aria-hidden="true" />}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className={styles.filter} role="group" aria-label="Zobrazit">
          <Button size="sm" weight="outline" pressed={attentionKeys === null} onClick={() => setAttentionKeys(null)}>
            Všichni ({listed.filter((r) => !isBlankNewRow(r)).length})
          </Button>
          <Button
            size="sm"
            weight="outline"
            pressed={attentionKeys !== null}
            onClick={() =>
              setAttentionKeys({
                needs: new Set(
                  listed.filter((r) => needsAttention(r, errors.get(r.key), props.canEditIdentity)).map((r) => r.key),
                ),
                seen: new Set(listed.map((r) => r.key)),
              })
            }
          >
            Chybí údaje ({attentionCount})
          </Button>
        </div>
        <Button
          className={styles.add}
          variant="primary"
          startIcon={<UserPlus size={16} aria-hidden="true" />}
          onClick={jumpToForm}
        >
          Přidat člena
        </Button>
      </div>

      <div className={styles.list} ref={listRef}>
        <div className={styles.head} aria-hidden="true">
          <span />
          <span>Člen</span>
          <span>Narození · RČ</span>
          <span>Stát</span>
          <span>Adresa</span>
          <span>Role</span>
          <span className={styles.statusCol}>Stav</span>
          <span />
        </div>
        {shown.map((row) => (
          <PersonItem
            key={row.key}
            row={row}
            rowErrors={errors.get(row.key) ?? NO_ERRORS}
            open={openKey === row.key}
            hit={hits.has(row.key)}
            canEditIdentity={props.canEditIdentity}
            onToggle={toggle}
            onCell={props.onCell}
            onRoles={props.onRoles}
            onRemove={props.onRemove}
            onMerge={props.onMerge ?? noMerge}
          />
        ))}
        {shown.length === 0 && (
          <p className={styles.empty}>
            {attentionKeys ? "Všichni mají vyplněny všechny potřebné údaje." : "Soupiska je zatím prázdná."}
          </p>
        )}

        {formRow && (
          <div
            className={styles.newMember}
            ref={formRef}
            onBlur={onFormBlur}
            data-row-key={formRow.key}
            role="group"
            aria-labelledby="new-member-title"
          >
            <div className={styles.newHead}>
              <strong id="new-member-title">
                <UserPlus size={16} aria-hidden="true" /> Nový člen
              </strong>
              {props.suggest && <span>Při psaní příjmení nabídneme lidi z minulé sezóny.</span>}
            </div>
            <PersonForm
              key={formRow.key}
              row={formRow}
              rowErrors={errors.get(formRow.key) ?? NO_ERRORS}
              isNew
              canEditIdentity={props.canEditIdentity}
              onCell={formOnCell}
              onRoles={formOnRoles}
              suggest={props.suggest}
              onPick={pickInForm}
            />
            <div className={styles.formFoot}>
              <span className={styles.spacer} />
              {/* No focus move on press: Safari and Firefox on macOS do not
                  focus a clicked button, so the input's blur would reach
                  onFormBlur with no relatedTarget and add the person before
                  „Smazat" runs. */}
              <Button
                weight="outline"
                disabled={isBlankNewRow(formRow)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={clearForm}
              >
                Smazat
              </Button>
              <Button
                variant="primary"
                disabled={isBlankNewRow(formRow)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={addFromForm}
              >
                Přidat
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

interface PersonItemProps {
  row: DraftRow;
  rowErrors: RowErrors;
  open: boolean;
  hit: boolean;
  canEditIdentity: boolean;
  onToggle: (key: string) => void;
  onCell: DraftRosterProps["onCell"];
  onRoles: DraftRosterProps["onRoles"];
  onRemove: DraftRosterProps["onRemove"];
  onMerge: NonNullable<DraftRosterProps["onMerge"]>;
}

/**
 * Memoized: the page's callbacks are stable and rowErrors comes from the
 * memoized map (or the shared NO_ERRORS), so a keystroke re-renders only the
 * person being typed into.
 */
const PersonItem = memo(function PersonItem({
  row,
  rowErrors,
  open,
  hit,
  canEditIdentity,
  onToggle,
  onCell,
  onRoles,
  onRemove,
  onMerge,
}: PersonItemProps) {
  const p = row.person;
  const st = status(row, rowErrors);
  const show = !row.removed;
  const name = `${p.last_name} ${p.first_name}`.trim() || "Nový člen";
  const town = [p.postal_code, p.city].filter((v) => v.trim()).join(" ");
  const missing = (label: string) => (show ? <span className={styles.missing}>chybí {label}</span> : null);
  const formId = `person-form-${row.key}`;
  const roles: RoleBadgeRole[] = [...(row.isLeader ? (["leader"] as const) : []), ...row.roles];
  const gaps = missingFields(row);
  const rcIssue = show ? birthNumberIssue(row, rowErrors) : null;
  const summary = [
    p.birth_date ? formatDate(p.birth_date) : "",
    p.birth_number,
    p.country_code,
    [p.street, town].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(" · ");

  const cls = [styles.person, open && styles.open, row.removed && styles.removed, hit && styles.hit]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls} data-row-key={row.key}>
      {/* eslint-disable-next-line @marekl/adminer/prefer-lib-form-controls -- the whole person line is the disclosure toggle, a full-width row of columns; the lib Button draws a labelled action and Accordion's header keeps everything but the title outside its toggle */}
      <button
        type="button"
        className={styles.line}
        aria-expanded={open}
        aria-controls={open ? formId : undefined}
        onClick={() => onToggle(row.key)}
      >
        <span className={styles.chevron} aria-hidden="true">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        <span className={styles.colName}>
          <span className={styles.name}>{name}</span>
          {row.isLeader && <span className={styles.sub}>Vedoucí</span>}
          <span className={styles.summary}>
            {summary}
            {show && gaps.length > 0 && <span className={styles.missing}> · chybí {gaps.join(", ")}</span>}
            {/* aria-hidden: the number already carries the problem for a screen reader. */}
            {rcIssue && <span className={styles.missing} aria-hidden="true"> · RČ neodpovídá</span>}
          </span>
        </span>
        <span className={styles.col}>
          {p.birth_date ? <span className={styles.mono}>{formatDate(p.birth_date)}</span> : missing("datum")}
          {p.birth_number ? (
            rcIssue ? (
              <span className={`${styles.mono} ${styles.missing}`} title={rcIssue}>
                <TriangleAlert size={12} aria-hidden="true" /> {p.birth_number}
                <span className={styles.srOnly}> ({rcIssue})</span>
              </span>
            ) : (
              <span className={styles.mono}>{p.birth_number}</span>
            )
          ) : (
            missing("RČ")
          )}
        </span>
        <span className={styles.col}>
          <span className={styles.mono} title={COUNTRY_OPTIONS.find((o) => o.value === p.country_code)?.label}>
            {p.country_code}
          </span>
        </span>
        <span className={styles.col}>
          {p.street ? <span>{p.street}</span> : missing("ulice")}
          {town ? (
            <span>
              {town}
              {show && !p.postal_code.trim() && <span className={styles.missing}> · chybí PSČ</span>}
              {show && !p.city.trim() && <span className={styles.missing}> · chybí město</span>}
            </span>
          ) : (
            missing("PSČ a město")
          )}
        </span>
        <span className={styles.colRoles}>
          {roles.length > 0 ? (
            roles.map((r) => <RoleBadge key={r} role={r} />)
          ) : (
            <span className={styles.dim}>Bez role</span>
          )}
        </span>
        <span className={styles.statusCol}>
          {st && <Badge variant={st.variant}>{st.label}</Badge>}
        </span>
        <span className={styles.action} aria-hidden="true">
          {open ? <X size={14} aria-hidden="true" /> : <Pencil size={14} aria-hidden="true" />}
          {open ? "Zavřít" : "Upravit"}
        </span>
      </button>
      {open && (
        <div id={formId} className={styles.openBody}>
          <PersonForm
            row={row}
            rowErrors={rowErrors}
            canEditIdentity={canEditIdentity}
            onCell={onCell}
            onRoles={onRoles}
            onMerge={onMerge}
          />
          <div className={styles.formFoot}>
            {row.orphaned ? (
              <>
                <Button variant="primary" onClick={() => onMerge({ type: "REREGISTER", key: row.key })}>
                  Zaregistrovat znovu
                </Button>
                <Button onClick={() => onMerge({ type: "DROP_STAGED", key: row.key })}>Zahodit úpravy</Button>
              </>
            ) : row.duplicateOf ? (
              <>
                <Button variant="primary" onClick={() => onMerge({ type: "MERGE_DUPLICATE", key: row.key })}>
                  Spojit s osobou na soupisce
                </Button>
                <Button onClick={() => onMerge({ type: "KEEP_SEPARATE", key: row.key })}>
                  Ponechat jako novou osobu
                </Button>
              </>
            ) : (
              !row.isLeader && (
                <Button onClick={() => onRemove(row)}>
                  {row.removed ? "Vrátit na soupisku" : "Odebrat ze soupisky"}
                </Button>
              )
            )}
            <span className={styles.spacer} />
            <Button weight="outline" onClick={() => onToggle(row.key)}>
              Zavřít
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

interface PersonFormProps {
  row: DraftRow;
  rowErrors: RowErrors;
  isNew?: boolean;
  canEditIdentity: boolean;
  onCell: DraftRosterProps["onCell"];
  onRoles: DraftRosterProps["onRoles"];
  suggest?: DraftRosterProps["suggest"];
  onPick?: DraftRosterProps["onPick"];
  onMerge?: DraftRosterProps["onMerge"];
}

// A rodné číslo is warned about, never refused here: a number that does not
// check out (about 6 % of those on file) and, for now, an empty one. The one
// place either blocks is the team leader, server-side. See docs/rodne-cislo.md
// §5. Judged on blur, not per keystroke: a half-typed number is not a wrong one.
function birthNumberWarning(row: DraftRow, rowErrors: RowErrors, typing: boolean, isNew: boolean): string | undefined {
  if (rowErrors["person.birth_number"] || row.removed) return undefined;
  const p = row.person;
  const problem = birthNumberHint(
    p.birth_number,
    p.birth_date ? p.birth_date.format("YYYY-MM-DD") : undefined,
    { typing },
  );
  if (problem) return problem;
  // An EMPTY number is said even while the field has focus: holding back is
  // for a half-typed number, and opening a person puts the cursor straight
  // into this field when it is the first gap. Not under a form nobody has
  // started on, though: the new-member form opens empty.
  if (!p.birth_number.trim() && !(isNew && isBlankNewRow(row))) return BIRTH_NUMBER_MISSING_HINT;
  return undefined;
}

function PersonForm({
  row,
  rowErrors,
  isNew = false,
  canEditIdentity,
  onCell,
  onRoles,
  suggest,
  onPick,
  onMerge = noMerge,
}: PersonFormProps) {
  const p = row.person;
  const [rcTyping, setRcTyping] = useState(false);
  const locked = (f: keyof DraftPerson) => identityCellLocked(row, f, canEditIdentity);
  const disabled = row.removed;
  // A gap is only an ERROR once the row blocks the submit (errorsByRow); an
  // open form must not turn yellow the moment a surname is typed.
  const err = (f: keyof DraftPerson) => rowErrors[`person.${f}`];

  // A field that is locked shows its value as text: a greyed-out input reads
  // as broken, plain text reads as settled.
  const lockedValue = (label: string, value: string, mono = false, warning?: string) => (
    <div className={styles.locked}>
      <span className={styles.lockedLabel}>{label}</span>
      <span
        className={mono ? `${styles.lockedValue} ${styles.mono}` : styles.lockedValue}
        title={
          value
            ? "Vyplněno. Změnit ho může jen správce členské základny."
            : "Doplní správce členské základny."
        }
      >
        {value || "—"}
      </span>
      {warning && <span className={styles.lockedWarning}>{warning}</span>}
    </div>
  );
  const text = (f: keyof DraftPerson, label: string, cls = styles.narrow, opts: { warning?: string } = {}) =>
    locked(f) ? (
      <div className={cls}>{lockedValue(label, String(p[f] ?? ""), f === "birth_number", opts.warning)}</div>
    ) : (
      <div className={cls}>
        <Input
          label={label}
          value={String(p[f] ?? "")}
          disabled={disabled}
          error={err(f)}
          warning={opts.warning}
          onChange={(e) => onCell(row.key, f, e.target.value)}
          {...(f === "birth_number"
            ? { onFocus: () => setRcTyping(true), onBlur: () => setRcTyping(false) }
            : {})}
        />
      </div>
    );

  const surname =
    isNew && suggest && onPick ? (
      <div className={styles.wide}>
        <Autocomplete<Membership>
          label="Příjmení"
          value={p.last_name}
          onChange={(v) => onCell(row.key, "last_name", v)}
          loadSuggestions={(q) => Promise.resolve(suggest(q))}
          getSuggestionLabel={(m) => `${m.person.last_name} ${m.person.first_name}`}
          getSuggestionKey={(m) => m.person.id}
          onSelectSuggestion={(m) => onPick(row, m)}
          renderSuggestion={(m) => (
            <span className={styles.suggestion}>
              <strong>
                {m.person.last_name} {m.person.first_name}
              </strong>
              <span className={styles.suggestionMeta}>
                {m.person.birth_date ? `nar. ${formatDate(m.person.birth_date)}` : ""}
                {m.kind === "guest"
                  ? m.season_code
                    ? ` · v sezóně ${m.season_code} nečlen`
                    : " · nečlen"
                  : ""}
              </span>
            </span>
          )}
          minChars={2}
          debounceMs={100}
          error={err("last_name")}
        />
      </div>
    ) : (
      text("last_name", "Příjmení", styles.wide)
    );

  const conflicts = Object.entries(row.conflicts) as [keyof DraftPerson, FieldConflict][];

  return (
    <div className={styles.form} data-person-form="">
      {conflicts.length > 0 && (
        // Neither side is preselected (docs/rozpracovana-registrace.md §10):
        // a default would silently drop either the leader's typing or a
        // correction made by somebody else. Typing into the field answers too.
        <div className={styles.conflicts}>
          <Alert variant="warn" title="Někdo jiný mezitím změnil stejné údaje">
            {conflicts.map(([f, c]) => (
              <div key={f} className={styles.conflict}>
                <span className={styles.conflictText}>
                  <strong>{FIELD_LABELS[f]}</strong>: vaše „{displayValue(f, p[f])}“, na soupisce teď „
                  {displayValue(f, c.theirs)}“
                </span>
                <Button
                  size="sm"
                  onClick={() => onMerge({ type: "RESOLVE_CONFLICT", key: row.key, field: f, take: "mine" })}
                >
                  Ponechat moje
                </Button>
                <Button
                  size="sm"
                  onClick={() => onMerge({ type: "RESOLVE_CONFLICT", key: row.key, field: f, take: "theirs" })}
                >
                  Použít ze soupisky
                </Button>
              </div>
            ))}
          </Alert>
        </div>
      )}
      {surname}
      {text("first_name", "Jméno", styles.wide)}
      <div className={styles.wide}>
        <RolePicker row={row} rowErrors={rowErrors} onRoles={onRoles} />
      </div>
      {text("birth_number", "Rodné číslo", styles.wide, {
        warning: birthNumberWarning(row, rowErrors, rcTyping, isNew),
      })}
      <div className={styles.wide}>
        {locked("birth_date") ? (
          lockedValue("Datum narození", p.birth_date ? formatDate(p.birth_date) : "", true)
        ) : (
          <DatePicker
            label="Datum narození"
            value={p.birth_date}
            disabled={disabled}
            maxDate={dayjs()}
            onChange={(v) => onCell(row.key, "birth_date", v)}
            error={err("birth_date")}
          />
        )}
      </div>
      <div className={styles.wide}>
        <Select
          label="Státní příslušnost"
          value={p.country_code}
          disabled={disabled}
          error={err("country_code")}
          onChange={(e) => onCell(row.key, "country_code", e.target.value)}
        >
          {COUNTRY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </div>
      <div className={styles.rule} />
      {text("street", "Ulice a číslo popisné", styles.wide)}
      {text("postal_code", "PSČ")}
      {text("city", "Město")}
      {text("phone", "Telefon (nepovinné)", styles.wide)}
      {rowErrors.row && <p className={styles.rowError}>{rowErrors.row}</p>}
    </div>
  );
}

/**
 * A member may hold any combination of roles (a soutěžící who also coaches),
 * so this is a multi-select. The leader may hold none („pouze vedoucí") and
 * may not be an asistent.
 */
function RolePicker({
  row,
  rowErrors,
  onRoles,
}: {
  row: DraftRow;
  rowErrors: RowErrors;
  onRoles: DraftRosterProps["onRoles"];
}) {
  const options = row.isLeader ? LEADER_ROLE_OPTIONS : ROLE_OPTIONS;
  return (
    <Combobox<MembershipRole, MembershipRole>
      multiple
      label="Role v týmu"
      options={options}
      value={row.roles}
      getOptionLabel={(r) => ROLE_LABELS[r]}
      getOptionValue={(r) => r}
      getOptionKey={(r) => r}
      placeholder={row.isLeader ? "Pouze vedoucí" : "Bez role"}
      clearable={false}
      disabled={row.removed}
      error={rowErrors.roles}
      onChange={(roles) => onRoles(row.key, roles)}
    />
  );
}
